import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'icons/ChevronIcon';

export default function DataTable({
  columns,
  data,
  rowKey = (row, idx) => row.id ?? idx,
  onRowClick,
  enableSorting = false,
  onSort,
  sortField,
  sortOrder,
  emptyStateContent,
  pinnedTopRowContent, 
  headerClassName,
}) {
  const [hoveredRowKey, setHoveredRowKey] = useState(null);

  return (
    <div
      style={{
        borderRadius: 12,
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
        backgroundColor: '#FAFAFA',
      }}
    >
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
          }}
        >
          <thead className={headerClassName}>
            <tr>
              {columns.map((col) => {
                return (
                  <th
                    key={col.accessor}
                    style={{
                      width: col.width || 'auto',
                      cursor: col.sortable ? 'pointer' : 'default',
                      padding: '8px 16px',
                      fontSize: 13,
                      color: '#818181',
                      backgroundColor: '#E9E9E9',
                      textAlign: 'left',
                      fontWeight: 400,
                      borderBottom: 'none',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        onClick={(e) => {
                          if (enableSorting && col.sortable) {
                            onSort(col.accessor);
                          }
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          cursor: col.sortable ? 'pointer' : 'default',
                        }}
                      >
                        {col.renderHeader ? col.renderHeader() : col.header}
                        {col.sortable && (
                          <span style={{ marginLeft: 4 }}>
                            {col.accessor === sortField &&
                            sortOrder === 'asc' ? (
                              <ChevronUpIcon />
                            ) : (
                              <ChevronDownIcon />
                            )}
                          </span>
                        )}
                      </span>
                    </div>

                    {/* Filter stays outside the clickable area */}
                    {col.filter && (
                      <div style={{ marginTop: 4 }}>{col.filter}</div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pinnedTopRowContent && (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ padding: 0, border: 'none' }}
                >
                  {pinnedTopRowContent}
                </td>
              </tr>
            )}
            {data.length === 0 && emptyStateContent ? (
              <tr>
                <td 
                  colSpan={columns.length} 
                  style={{ 
                    padding: 0, 
                    border: 'none',
                    backgroundColor: 'transparent',
                  }}
                >
                  {emptyStateContent}
                </td>
              </tr>
            ) : (
              data.map((row, idx) => {
                const rowKeyValue = rowKey(row, idx);
                return (
                  <tr
                    key={rowKeyValue}
                    style={{
                      backgroundColor:
                        hoveredRowKey === rowKeyValue ? '#F9FAFB' : '#FFFFFF',
                      transition: 'background-color 0.15s ease-out',
                      cursor: onRowClick ? 'pointer' : 'default',
                    }}
                    onMouseEnter={() => setHoveredRowKey(rowKeyValue)}
                    onMouseLeave={() => setHoveredRowKey(null)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col) => {
                      const key = `${rowKeyValue}-${col.accessor}`;

                      return (
                        <TableData
                          key={key}
                          coldWidth={col.width}
                        >
                          {col.renderCell
                            ? col.renderCell(row, idx)
                            : col.accessor
                            ? row[col.accessor]
                            : null}
                        </TableData>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TableData = ({ coldWidth, children }) => (
  <td
    style={{
      width: coldWidth || 'auto',
      padding: '10px 16px',
      fontSize: 12,
      borderBottom: 'none',
      backgroundColor: 'inherit',
    }}
  >
    {children}
  </td>
);
